import React, { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import {
  BoltIcon,
  ChartBarSquareIcon,
  ChatBubbleLeftRightIcon,
  CheckIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import Blogs from './blogs';

/* ────────────────────────────────────────────────────────────
   Content
   ──────────────────────────────────────────────────────────── */

const productFeatures = [
  {
    title: 'Companies House intelligence',
    description: 'Search UK Companies House records and turn public filings into targeting signal before you ever pick up the phone.',
    icon: ShieldCheckIcon,
    featured: true,
    color: 'from-blue-500 to-indigo-600',
  },
  {
    title: 'Custom benefit reports',
    description: 'Auto-build a report for each prospect that spells out exactly where your service fits their business.',
    icon: DocumentTextIcon,
    color: 'from-purple-500 to-fuchsia-600',
  },
  {
    title: 'AI lead discovery',
    description: 'Pull targeted leads by location and sector, with criteria you set and lists generated in minutes.',
    icon: SparklesIcon,
    color: 'from-sky-500 to-blue-600',
  },
  {
    title: 'Outreach automation',
    description: 'Connect your own SMTP provider and send single or bulk outbound campaigns with credit-based controls.',
    icon: ChatBubbleLeftRightIcon,
    color: 'from-violet-500 to-purple-600',
  },
  {
    title: 'Enrichment & call scripts',
    description: 'Enrich records with deeper company context and generate call scripts so reps open every call prepared.',
    icon: BoltIcon,
    color: 'from-indigo-500 to-blue-600',
  },
  {
    title: 'AI sales reporting',
    description: 'Track lead quality and outreach performance with reports built for pipeline reviews, not vanity metrics.',
    icon: ChartBarSquareIcon,
    color: 'from-fuchsia-500 to-pink-600',
  },
];

const processSteps = [
  {
    step: '01',
    title: 'Search Companies House',
    description: 'Pull public UK company filings — incorporation date, SIC code, filing history — to qualify accounts before outreach.',
  },
  {
    step: '02',
    title: 'Construct the report',
    description: 'Avenor writes a benefit report per company, showing the specific case for why your service fits them.',
  },
  {
    step: '03',
    title: 'Reach out with proof',
    description: 'Send outreach backed by evidence instead of a generic template, and track what converts.',
  },
];

const creditCosts = [
  { label: 'Lead pull & enrichment', value: '3 credits', desc: 'Per company data pull from Companies House' },
];
  
const leadsCreditTiers = [
  { name: 'Starter', credits: 150, price: '£50', note: 'Best for small runs.', featured: false, discount: null },
  { name: 'Growth', credits: 300, price: '£100', note: 'Best for weekly outreach.', featured: false, discount: null },
  { name: 'Scale', credits: 1000, price: '£300', note: 'Priority credit processing.', featured: true, discount: '10% off' },
  { name: 'Pro', credits: 2000, price: '£565', note: 'Lower cost per credit, built for teams.', featured: false, discount: null },
  { name: 'Business', credits: 5000, price: '£1,350', note: 'High-volume outreach.', featured: false, discount: '15% off' },
  { name: 'Enterprise', credits: 10000, price: '£2,500', note: 'Maximum-scale package.', featured: false, discount: '25% off' },
];

const navigation = {
  company: [
    { name: 'Apply', href: '/qualification' },
    { name: 'Login', href: '/login' },
  ],
  legal: [
    { name: 'Terms of Service', href: '/tosstatic' },
    { name: 'Privacy Policy', href: '/tosstatic' },
  ],
  social: [
    {
      name: 'Facebook',
      href: 'https://www.facebook.com/people/Avenor-AI-UK-Sales-Intelligence/61590607235140/?mibextid=wwXIfr&rdid=IlD8ZpqLQACCcZoc&share_url=https%3A%2F%2Fwww.facebook.com%2Fshare%2F1BGmWJTQ3P%2F%3Fmibextid%3DwwXIfr',
      icon: (props) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path
            fillRule="evenodd"
            d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    {
      name: 'Instagram',
      href: 'https://www.instagram.com/avenor_ai?igsh=ZDZmb3VtcXFlNHYw',
      icon: (props) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path
            fillRule="evenodd"
            d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    {
      name: 'LinkedIn',
      href: 'https://www.linkedin.com/company/avenor-ai-uk-sales-intelligence',
      icon: (props) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path d="M4.98 3.5A2.48 2.48 0 1 1 2.5 5.98 2.48 2.48 0 0 1 4.98 3.5ZM3 8.75h3.95V21H3V8.75Zm6.36 0H13.1v1.68h.06c.56-1.06 1.92-2.18 3.96-2.18C21.36 8.25 22 10.55 22 13.54V21h-3.95v-6.6c0-1.57-.03-3.59-2.19-3.59-2.19 0-2.52 1.71-2.52 3.48V21H9.36V8.75Z" />
        </svg>
      ),
    },
  ],
};

const AVENOR_COOKIE_CONSENT_KEY = 'avenorCookieConsent';
const AVENOR_APP_URL = 'https://avenor.insightos.co.uk';

/* ────────────────────────────────────────────────────────────
   Live lookup panel — the hero's one bold moment.
   Cycles through a company record resolving into a report line.
   ──────────────────────────────────────────────────────────── */

const LOOKUP_RECORDS = [
  { name: 'HARLOW & VANCE BUILD LTD', number: '09184627', sic: '41202 — Construction of domestic buildings', status: 'Active', match: 'Fits: field workforce management' },
  { name: 'NORTHGATE CARE SERVICES LTD', number: '11837205', sic: '87300 — Residential care activities', status: 'Active', match: 'Fits: rota & compliance tooling' },
  { name: 'BRAMLEY & CO ACCOUNTANTS LLP', number: '07726190', sic: '69201 — Accounting & auditing', status: 'Active', match: 'Fits: client reporting automation' },
];

function LookupPanel() {
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState('scanning'); // scanning -> matched
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (reduceMotion) return undefined;
    const scanTimer = setTimeout(() => setPhase('matched'), 1400);
    const nextTimer = setTimeout(() => {
      setPhase('scanning');
      setIndex((i) => (i + 1) % LOOKUP_RECORDS.length);
    }, 4200);
    return () => {
      clearTimeout(scanTimer);
      clearTimeout(nextTimer);
    };
  }, [index, reduceMotion]);

  const record = LOOKUP_RECORDS[index];

  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/70 bg-white/80 shadow-[0_30px_90px_rgba(79,70,229,0.25)] backdrop-blur-xl">
      <div className="flex items-center justify-between border-b border-indigo-100/80 bg-gradient-to-r from-blue-50/90 via-white/60 to-purple-50/90 px-5 py-3">
        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-indigo-700">Companies House lookup</span>
        <span className="flex items-center gap-1.5 font-mono text-[11px] font-semibold text-indigo-600">
          <span className={`h-1.5 w-1.5 rounded-full ${phase === 'matched' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
          {phase === 'matched' ? 'matched' : 'scanning'}
        </span>
      </div>

      <div className="space-y-4 px-5 py-6">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-slate-500">Company</p>
          <p className="mt-1 font-display text-lg font-semibold text-slate-900">{record.name}</p>
        </div>

        <div className="grid grid-cols-2 gap-4 border-t border-indigo-100 pt-4">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-slate-500">Company no.</p>
            <p className="mt-1 font-mono text-sm font-semibold text-indigo-700">{record.number}</p>
          </div>
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-slate-500">Status</p>
            <p className="mt-1 font-mono text-sm font-semibold text-emerald-600">{record.status}</p>
          </div>
        </div>

        <div className="border-t border-indigo-100 pt-4">
          <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-slate-500">SIC code</p>
          <p className="mt-1 text-sm text-slate-700">{record.sic}</p>
        </div>

        <div
          className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 transition-opacity duration-500 ${
            phase === 'matched' ? 'border-emerald-300/70 bg-emerald-50 opacity-100' : 'border-slate-200 bg-slate-50 opacity-0'
          }`}
        >
          <CheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
          <p className="text-sm font-medium text-emerald-700">{record.match}</p>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   Page
   ──────────────────────────────────────────────────────────── */

export default function Home() {
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const fontsInjected = useRef(false);
  const applyUrl = `${AVENOR_APP_URL}/qualification`;
  const loginUrl = `${AVENOR_APP_URL}/login`;

  const collectSalesVisitor = async () => {
    try {
      const FingerprintJS = (await import('@fingerprintjs/fingerprintjs')).default;
      const fp = await FingerprintJS.load();
      const result = await fp.get();

      await fetch('https://api.insightos.co.uk/api/leads/salesvisitor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitorId: result.visitorId }),
      });
    } catch (error) {
      console.error('salesvisitor capture failed', error);
    }
  };

  useEffect(() => {
    if (fontsInjected.current) return;
    fontsInjected.current = true;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap';
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    const hasConsent = localStorage.getItem(AVENOR_COOKIE_CONSENT_KEY);

    if (hasConsent === 'true') {
      collectSalesVisitor();
    } else if (hasConsent !== 'false') {
      setShowCookieBanner(true);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem(AVENOR_COOKIE_CONSENT_KEY, 'true');
    setShowCookieBanner(false);
    collectSalesVisitor();
  };

  const declineCookies = () => {
    localStorage.setItem(AVENOR_COOKIE_CONSENT_KEY, 'false');
    setShowCookieBanner(false);
  };

  return (
    <div
      className="relative min-h-screen overflow-hidden text-slate-900"
      style={{
        fontFamily: "'Inter', system-ui, sans-serif",
        backgroundImage: "linear-gradient(135deg, #ffffff 0%, #dbeafe 35%, #c7d2fe 60%, #ddb3ff 100%)",
        backgroundRepeat: 'no-repeat',
      }}
    >
      <style>{`
        .font-display { font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif; }
        .font-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; }
        .registry-grid {
          background-image: linear-gradient(rgba(79,70,229,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(79,70,229,0.06) 1px, transparent 1px);
          background-size: 42px 42px;
        }
        .gradient-text {
          background-image: linear-gradient(90deg, #4f46e5, #7c3aed, #db2777);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        @media (prefers-reduced-motion: reduce) {
          * { animation-duration: 0.001ms !important; animation-iteration-count: 1 !important; transition-duration: 0.001ms !important; }
        }
      `}</style>

      {/* Ambient brand glow — vivid, colorful accent washes */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[620px] bg-[radial-gradient(55%_50%_at_15%_0%,rgba(79,70,229,0.22),transparent_70%),radial-gradient(45%_45%_at_90%_15%,rgba(219,39,119,0.16),transparent_70%),radial-gradient(50%_40%_at_50%_100%,rgba(14,165,233,0.14),transparent_70%)]" />
      <div className="registry-grid pointer-events-none absolute inset-0 opacity-60 [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,black,transparent)]" />

      {showCookieBanner && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 px-4 backdrop-blur-sm">
          <div className="relative w-full max-w-3xl space-y-4 rounded-2xl border border-white/70 bg-white p-6 shadow-2xl">
            <button
              onClick={declineCookies}
               className="absolute right-4 top-4 rounded-full bg-slate-100 p-2 text-slate-600 transition hover:bg-slate-200 hover:text-slate-900"
              aria-label="Close"
              type="button"
            >
              ✕
            </button>
            <div className="max-h-[75vh] space-y-4 overflow-y-auto pr-1">
              <h2 className="font-display text-xl font-semibold text-slate-900">We use cookies and similar technologies</h2>
              <p className="text-sm text-slate-700">
                We use cookies and similar technologies, including device fingerprinting, to secure our platform,
                prevent fraud, and improve your experience.
              </p>
              <p className="text-sm text-slate-700">
                Some of these technologies may uniquely identify your device. We only use non-essential technologies with your consent.
              </p>
              <p className="text-sm text-slate-700">
                You can accept all, reject non-essential, or manage your preferences at any time. See our{' '}
                <a href="/privacy" className="font-medium text-indigo-600 underline underline-offset-2">Privacy Policy</a> and{' '}
                <a href="/cookie-policy" className="font-medium text-indigo-600 underline underline-offset-2">Cookie Policy</a>.
              </p>

              <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-4">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-800">Device fingerprinting and security</h3>
                <p className="mt-2 text-sm text-slate-700">
                  Avenor uses device fingerprinting technologies provided by FingerprintJS to help protect our platform,
                  users, and services from fraud, abuse, and unauthorised access.
                </p>

                <h4 className="mt-3 text-sm font-semibold text-slate-800">What data we collect</h4>
                <p className="mt-1 text-sm text-slate-700">This may include:</p>
                <ul className="mt-1 list-inside list-disc text-sm text-slate-700">
                  <li>IP address</li>
                  <li>Browser type and version</li>
                  <li>Operating system and device characteristics</li>
                  <li>Network and connection data</li>
                  <li>Other technical identifiers derived from your device configuration</li>
                </ul>
                <p className="mt-2 text-sm text-slate-700">
                  These elements may be combined to create a unique identifier (a &ldquo;fingerprint&rdquo;) for your device.
                </p>

                <h4 className="mt-3 text-sm font-semibold text-slate-800">Purpose of processing</h4>
                <ul className="mt-1 list-inside list-disc text-sm text-slate-700">
                  <li>Fraud detection and prevention</li>
                  <li>Identifying suspicious or malicious activity</li>
                  <li>Protecting user accounts and platform integrity</li>
                  <li>Ensuring the security and reliability of our services</li>
                </ul>

                <h4 className="mt-3 text-sm font-semibold text-slate-800">Legal basis</h4>
                <p className="mt-1 text-sm text-slate-700">
                  Where required under applicable data protection laws (including UK GDPR and EU GDPR), we rely on your consent
                  before processing this data.
                </p>
                <p className="mt-1 text-sm text-slate-700">
                  In limited cases, we may also rely on our legitimate interests to ensure the security of our services,
                  provided such interests are not overridden by your rights and freedoms.
                </p>

                <h4 className="mt-3 text-sm font-semibold text-slate-800">Data retention</h4>
                <p className="mt-1 text-sm text-slate-700">
                  We retain fingerprinting data only for as long as necessary to fulfil the purposes described above,
                  and in line with our security and legal obligations.
                </p>

                <h4 className="mt-3 text-sm font-semibold text-slate-800">Third-party processing</h4>
                <p className="mt-1 text-sm text-slate-700">
                  Fingerprinting data may be processed by our service provider, FingerprintJS, in accordance with their
                  privacy policies and applicable data protection laws.
                </p>

                <h4 className="mt-3 text-sm font-semibold text-slate-800">Your rights</h4>
                <ul className="mt-1 list-inside list-disc text-sm text-slate-700">
                  <li>Access your personal data</li>
                  <li>Request correction or deletion</li>
                  <li>Object to processing</li>
                  <li>Withdraw your consent at any time</li>
                </ul>
                <p className="mt-2 text-sm text-slate-700">
                  You can exercise these rights by contacting us using the details in this policy.
                </p>
              </div>

              <div className="rounded-xl border border-purple-100 bg-purple-50/60 p-4">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-800">Cookies and similar technologies</h3>
                <p className="mt-2 text-sm text-slate-700">Avenor uses cookies and similar technologies to operate and improve our services.</p>

                <h5 className="mt-3 text-sm font-semibold text-slate-800">Strictly necessary</h5>
                <p className="mt-1 text-sm text-slate-700">Required for the operation of our website and cannot be disabled.</p>

                <h5 className="mt-2 text-sm font-semibold text-slate-800">Functional</h5>
                <p className="mt-1 text-sm text-slate-700">Help improve usability and remember your preferences.</p>

                <h5 className="mt-2 text-sm font-semibold text-slate-800">Security and fraud prevention</h5>
                <p className="mt-1 text-sm text-slate-700">
                  We use advanced technologies, including device fingerprinting, to detect and prevent fraudulent or abusive activity.
                  Unlike traditional cookies, fingerprinting does not store information on your device — it analyses technical
                  characteristics of your browser and device to create a unique identifier.
                </p>

                <h4 className="mt-3 text-sm font-semibold text-slate-800">Consent</h4>
                <p className="mt-1 text-sm text-slate-700">
                  Where required by law, we request your consent before using non-essential cookies or similar technologies,
                  including fingerprinting. You can manage or withdraw your consent at any time through our cookie settings.
                </p>

                <h4 className="mt-3 text-sm font-semibold text-slate-800">Third parties</h4>
                <p className="mt-1 text-sm text-slate-700">
                  Some technologies are provided by third-party services, including FingerprintJS, which may process data on our behalf.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={declineCookies}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                type="button"
              >
                Reject all
              </button>
              <button
                onClick={acceptCookies}
                className="rounded-lg bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-purple-500/30 transition hover:opacity-90"
                type="button"
              >
                Accept all
              </button>
            </div>
          </div>
        </div>
      )}

      <header className="sticky top-0 z-40 border-b border-white/60 bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/avenor.png" alt="Avenor" className="h-8 w-8 rounded-md border border-indigo-100 bg-white p-1 shadow-sm" />
            <div className="leading-tight">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-indigo-500">InsightOS</p>
              <h1 className="font-display text-base font-semibold text-slate-900">Avenor</h1>
            </div>
          </div>

          <nav className="hidden items-center gap-8 sm:flex">
            <a href="#product" className="text-sm font-medium text-slate-600 transition hover:text-indigo-600">Product</a>
            <a href="#pricing" className="text-sm font-medium text-slate-600 transition hover:text-indigo-600">Pricing</a>
            <a href="#blog" className="text-sm font-medium text-slate-600 transition hover:text-indigo-600">Blog</a>
          </nav>

          <div className="flex items-center gap-3">
            <a href={loginUrl} className="hidden text-sm font-medium text-slate-600 transition hover:text-indigo-600 sm:block">
              Login
            </a>
            <a
              href={applyUrl}
              className="rounded-lg bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-purple-500/30 transition hover:opacity-90"
            >
              Apply
            </a>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto w-full max-w-6xl px-4 sm:px-6">

        {/* ── Hero ── */}
        <section className="grid gap-10 pb-16 pt-14 sm:pt-20 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:gap-14">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-white/80 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.14em] text-indigo-700 shadow-sm">
              Built for UK Companies House data — GB market only
            </p>
            <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.08] text-slate-900 sm:text-5xl lg:text-[3.25rem]">
              Turn public company filings into <span className="gradient-text">sales conversations</span> that already know what a prospect needs.
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-700">
              Avenor searches UK Companies House data and writes a report for each prospect showing exactly how
              your product or service fits their business — before you send the first message.
            </p>
            <p className="mt-3 max-w-xl text-sm font-medium text-slate-600">
              Pay as you go. No subscription lock-in — buy credits, use them when you need them.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href={applyUrl}
                className="rounded-lg bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-purple-500/30 transition hover:opacity-90"
              >
                Apply for access
              </a>
              <a href={loginUrl} className="text-sm font-medium text-indigo-700 transition hover:text-indigo-900">
                Go to login →
              </a>
            </div>

            <dl className="mt-12 grid max-w-md grid-cols-3 gap-6 border-t border-indigo-100 pt-6">
              <div>
                <dt className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">Per lead pull</dt>
                <dd className="mt-1 font-display text-lg font-semibold text-indigo-700">1 credit</dd>
              </div>
              <div>
                <dt className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">Reports</dt>
                <dd className="mt-1 font-display text-lg font-semibold text-purple-700">Included</dd>
              </div>
              <div>
                <dt className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">Credits expire</dt>
                <dd className="mt-1 font-display text-lg font-semibold text-pink-600">Never</dd>
              </div>
            </dl>
          </div>

          <LookupPanel />
        </section>

        {/* ── Process ── */}
        <section id="product" className="border-t border-indigo-100 py-16">
          <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-indigo-600">How it works</p>
          <div className="mt-8 grid gap-8 md:grid-cols-3 md:gap-10">
            {processSteps.map((s, i) => {
              const stepColors = ['from-blue-500 to-indigo-600', 'from-indigo-500 to-purple-600', 'from-purple-500 to-pink-600'];
              return (
                <div key={s.step}>
                  <span className={`inline-flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br ${stepColors[i % stepColors.length]} font-display text-sm font-bold text-white shadow-md`}>
                    {s.step}
                  </span>
                  <h3 className="mt-3 font-display text-lg font-semibold text-slate-900">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.description}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Features ── */}
        <section className="border-t border-indigo-100 py-16">
          <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-indigo-600">Platform</p>
          <h3 className="mt-3 max-w-md font-display text-2xl font-semibold text-slate-900">Everything a sales team needs to work UK company data</h3>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {productFeatures.map((feature) => {
              const Icon = feature.icon;
              return (
                <article
                  key={feature.title}
                  className={`rounded-xl border border-white/70 bg-white/80 p-5 shadow-[0_8px_30px_rgba(79,70,229,0.12)] backdrop-blur-sm transition hover:shadow-[0_12px_40px_rgba(79,70,229,0.2)] ${
                    feature.featured ? 'lg:col-span-2 ring-1 ring-indigo-200' : ''
                  }`}
                >
                  <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br ${feature.color} shadow-sm`}>
                    <Icon className="h-5 w-5 text-white" />
                  </span>
                  <h4 className="mt-3 font-display text-base font-semibold text-slate-900">{feature.title}</h4>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{feature.description}</p>
                </article>
              );
            })}
          </div>
        </section>

        {/* ── Blog ── */}
        <section id="blog" className="border-t border-indigo-100 py-16">
          <Blogs />
        </section>

        {/* ── Pricing ── */}
        <section id="pricing" className="border-t border-indigo-100 py-16">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-indigo-600">Pricing</p>
              <h3 className="mt-3 font-display text-2xl font-semibold text-slate-900">One credit pack, every feature</h3>
              <p className="mt-2 max-w-lg text-sm text-slate-600">
                Credits are shared across the whole platform and never expire. No subscriptions.
              </p>
            </div>
            <a
              href={applyUrl}
              className="rounded-lg bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-purple-500/30 transition hover:opacity-90"
            >
              Apply
            </a>
          </div>

          <div className="mt-8 grid gap-3 sm:grid-cols-3">
            {creditCosts.map((c) => (
              <div key={c.label} className="rounded-xl border border-white/70 bg-white/80 p-4 shadow-sm">
                <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">{c.label}</p>
                <p className="mt-1 font-display text-xl font-semibold text-indigo-700">{c.value}</p>
                <p className="mt-1 text-sm text-slate-600">{c.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {leadsCreditTiers.map((tier) => (
              <div
                key={tier.name}
                className={`rounded-xl border p-5 shadow-sm ${
                  tier.featured
                    ? 'border-indigo-300 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 shadow-[0_10px_35px_rgba(147,51,234,0.18)]'
                    : 'border-white/70 bg-white/80'
                }`}
              >
                {tier.discount && (
                  <span className="mb-2 inline-block rounded-full bg-gradient-to-r from-purple-500 to-pink-500 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-white">
                    {tier.discount}
                  </span>
                )}
                <p className="text-sm font-medium text-slate-700">{tier.name}</p>
                <p className="mt-3 font-display text-2xl font-semibold text-slate-900">{tier.price}</p>
                <p className="mt-1 font-mono text-xs text-indigo-600">{tier.credits.toLocaleString()} credits</p>
                <p className="mt-2 text-sm text-slate-600">{tier.note}</p>
              </div>
            ))}
          </div>
        </section>
      </main>

      <footer className="relative z-10 border-t border-indigo-100 bg-white/60">
        <div className="mx-auto max-w-6xl px-4 pb-10 pt-14 sm:px-6">
          <div className="grid gap-10 lg:grid-cols-[1.3fr_1fr_1fr_1fr]">
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <img src="/avenor.png" alt="Avenor" className="h-8 w-8 rounded-md border border-indigo-100 bg-white p-1 shadow-sm" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-indigo-500">InsightOS</p>
                  <p className="font-display text-sm font-semibold text-slate-900">Avenor</p>
                </div>
              </div>
              <p className="max-w-xs text-sm text-slate-600">
                AI-powered sales intelligence for UK businesses — Companies House lookup, benefit reports, enrichment
                and outreach in one credit-based platform.
              </p>
              <div className="flex gap-4">
                {navigation.social.map((item) => (
                  <a key={item.name} href={item.href} className="text-indigo-400 transition hover:text-indigo-700">
                    <span className="sr-only">{item.name}</span>
                    <item.icon aria-hidden="true" className="h-5 w-5" />
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-slate-900">Platform</h3>
              <ul className="mt-4 space-y-2.5">
                {navigation.company.map((item) => (
                  <li key={item.name}>
                    <a href={item.href} className="text-sm text-slate-600 transition hover:text-indigo-700">
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-slate-900">Contact</h3>
              <ul className="mt-4 space-y-2.5 text-sm text-slate-600">
                <li>
                  <a href="mailto:support@insightos.co.uk" className="transition hover:text-indigo-700">
                    support@insightos.co.uk
                  </a>
                </li>
                <li className="text-slate-400">Onboarding, account help, and production support.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-slate-900">Legal</h3>
              <ul className="mt-4 space-y-2.5">
                {navigation.legal.map((item) => (
                  <li key={item.name}>
                    <a href={item.href} className="text-sm text-slate-600 transition hover:text-indigo-700">
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-12 border-t border-indigo-100 pt-6">
            <p className="text-xs text-slate-400">
              &copy; {new Date().getFullYear()} Avenor Software Ltd. All rights reserved. Registered in England &amp; Wales.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
